import { geminiLiveUrl, api } from "./api";

export type VoiceState = "idle" | "connecting" | "live" | "error";

export interface VoiceController {
  start: () => Promise<void>;
  stop: () => void;
  state: () => VoiceState;
}

export interface TranscriptLine {
  role: "user" | "model";
  text: string;
  final: boolean;
}

const DOPE_SYSTEM_PROMPT = `You are DOPE — Data on Previous Engagements. You are the tactical institutional memory for this engineering team. You live in this repo. You build this repo.

You have direct GitHub access via function calls. Use them without being asked.

Your capabilities:
- list_projects: List all accessible repos/projects
- create_repo: Create a new GitHub repo for a project
- read_file: Read any file from a repo (README, ADRs, docs) — use this to load context when the user mentions a project
- list_adrs: List ADRs in a repo
- commit_adr: Write and commit an ADR to a repo
- commit_file: Write any file to a repo

When the user mentions a project by name, call list_projects to find it, then read_file on its README.md and list_adrs to load context. Never say you can't see a repo without calling list_projects first.

If read_file returns found=false and list_adrs returns empty, the repo is empty. Say so — "Looks like Guild is empty, let's build it out" — then offer to scaffold a README and first ADR. Do not wait for the user to ask.

Tone: direct, brief, no filler. Never tell the user to run git commands manually — you can do it.`;

const DOPE_TOOLS = [
  {
    functionDeclarations: [
      {
        name: "list_projects",
        description: "List all GitHub repositories owned by the team.",
        parameters: { type: "object", properties: {} },
      },
      {
        name: "create_repo",
        description: "Create a new GitHub repository for a project.",
        parameters: {
          type: "object",
          properties: {
            name: { type: "string", description: "Repository name (no spaces)." },
            description: { type: "string", description: "Short description." },
            private: { type: "boolean", description: "Whether the repo is private." },
          },
          required: ["name"],
        },
      },
      {
        name: "list_adrs",
        description: "List Architecture Decision Records in a repository.",
        parameters: {
          type: "object",
          properties: { repo: { type: "string", description: "Repository name." } },
          required: ["repo"],
        },
      },
      {
        name: "commit_adr",
        description: "Write and commit an Architecture Decision Record to a repository.",
        parameters: {
          type: "object",
          properties: {
            repo: { type: "string", description: "Repository name." },
            title: { type: "string", description: "ADR title." },
            body: { type: "string", description: "Full ADR body in markdown." },
            status: { type: "string", description: "Proposed | Accepted | Deprecated | Superseded" },
          },
          required: ["repo", "title", "body"],
        },
      },
      {
        name: "read_file",
        description: "Read the contents of any file in a GitHub repository to load context.",
        parameters: {
          type: "object",
          properties: {
            repo: { type: "string", description: "Repository name." },
            path: { type: "string", description: "File path, e.g. README.md." },
            ref: { type: "string", description: "Branch or commit ref. Defaults to main." },
          },
          required: ["repo", "path"],
        },
      },
      {
        name: "commit_file",
        description: "Write any file to a GitHub repository.",
        parameters: {
          type: "object",
          properties: {
            repo: { type: "string", description: "Repository name." },
            path: { type: "string", description: "File path within the repo." },
            content: { type: "string", description: "Full file content." },
            message: { type: "string", description: "Git commit message." },
          },
          required: ["repo", "path", "content", "message"],
        },
      },
    ],
  },
];

const CAPTURE_RATE = 16000;
const PLAYBACK_RATE = 24000;
const GEMINI_MODEL = "models/gemini-3.1-flash-live-preview";
const CHUNK_SIZE = 4096;

function floatToPcm16(float32: Float32Array): ArrayBuffer {
  const int16 = new Int16Array(float32.length);
  float32.forEach((sample, i) => {
    const s = Math.max(-1, Math.min(1, sample));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  });
  return int16.buffer;
}

function bufToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let b = "";
  bytes.forEach((byte) => { b += String.fromCharCode(byte); });
  return btoa(b);
}

function base64ToPcm16Float(b64: string): Float32Array<ArrayBuffer> {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const int16 = new Int16Array(bytes.buffer);
  const f32 = new Float32Array(int16.length);
  int16.forEach((v, i) => { f32[i] = v / 32768; });
  return f32;
}

export function createVoiceController(
  onState: (s: VoiceState, msg?: string) => void,
  onTranscript?: (line: TranscriptLine) => void,
): VoiceController {
  let ws: WebSocket | null = null;
  let stream: MediaStream | null = null;
  let captureCtx: AudioContext | null = null;
  let processor: ScriptProcessorNode | null = null;
  let playCtx: AudioContext | null = null;
  let nextPlayAt = 0;
  let currentState: VoiceState = "idle";

  const set = (s: VoiceState, msg?: string) => {
    currentState = s;
    onState(s, msg);
  };

  function cleanup() {
    processor?.disconnect();
    processor = null;
    if (captureCtx) { captureCtx.close(); captureCtx = null; }
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    ws = null;
    nextPlayAt = 0;
  }

  function scheduleAudio(f32: Float32Array<ArrayBuffer>) {
    if (!playCtx) {
      playCtx = new AudioContext({ sampleRate: PLAYBACK_RATE });
    }
    const ctx = playCtx;
    const buf = ctx.createBuffer(1, f32.length, PLAYBACK_RATE);
    buf.copyToChannel(f32, 0);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const now = ctx.currentTime;
    const start = Math.max(now, nextPlayAt);
    src.start(start);
    nextPlayAt = start + buf.duration;
  }

  function startCapture() {
    captureCtx = new AudioContext({ sampleRate: CAPTURE_RATE });
    const source = captureCtx.createMediaStreamSource(stream!);
    processor = captureCtx.createScriptProcessor(CHUNK_SIZE, 1, 1);
    processor.addEventListener("audioprocess", (ev) => {
      if (ws?.readyState !== WebSocket.OPEN) return;
      const pcm = floatToPcm16(ev.inputBuffer.getChannelData(0));
      ws.send(
        JSON.stringify({
          realtime_input: {
            media_chunks: [{ data: bufToBase64(pcm), mime_type: `audio/pcm;rate=${CAPTURE_RATE}` }],
          },
        }),
      );
    });
    source.connect(processor);
    processor.connect(captureCtx.destination);
  }

  async function handleMessage(raw: string) {
    let msg: Record<string, unknown>;
    try { msg = JSON.parse(raw); } catch { return; }

    console.debug("[gemini-live] recv:", JSON.stringify(msg).slice(0, 200));

    const err = (msg as { error?: { message?: string; code?: number } }).error;
    if (err) {
      set("error", `Gemini error ${err.code ?? ""}: ${err.message ?? raw}`);
      ws?.close();
      return;
    }

    if (msg.setupComplete !== undefined) {
      set("live");
      startCapture();
      return;
    }

    // Tool calls — execute via worker REST API and send responses back.
    type FunctionCall = { id: string; name: string; args: Record<string, unknown> };
    const toolCall = (msg as { toolCall?: { functionCalls?: FunctionCall[] } }).toolCall;
    if (toolCall?.functionCalls?.length) {
      const responses = await Promise.all(
        toolCall.functionCalls.map(async (fc) => {
          try {
            const result = await api.manage({ action: fc.name, input: fc.args });
            return { id: fc.id, response: { output: result } };
          } catch (e) {
            return { id: fc.id, response: { error: (e as Error).message } };
          }
        }),
      );
      ws?.send(JSON.stringify({ toolResponse: { functionResponses: responses } }));
      return;
    }

    type ServerContent = {
      modelTurn?: { parts?: { inlineData?: { mimeType?: string; data?: string } }[] };
      inputTranscription?: { text?: string };
      outputTranscription?: { text?: string };
      turnComplete?: boolean;
    };
    const sc = (msg as { serverContent?: ServerContent }).serverContent;

    if (sc?.inputTranscription?.text) {
      onTranscript?.({ role: "user", text: sc.inputTranscription.text, final: !!sc.turnComplete });
    }
    if (sc?.outputTranscription?.text) {
      onTranscript?.({ role: "model", text: sc.outputTranscription.text, final: !!sc.turnComplete });
    }

    const parts = sc?.modelTurn?.parts;
    if (parts) {
      for (const part of parts) {
        const d = part.inlineData;
        if (d?.data && d.mimeType?.startsWith("audio/")) {
          scheduleAudio(base64ToPcm16Float(d.data));
        }
      }
    }
  }

  return {
    state: () => currentState,

    async start() {
      if (currentState === "live" || currentState === "connecting") return;
      set("connecting");

      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        set("error", `mic denied: ${(err as Error).message}`);
        return;
      }

      ws = new WebSocket(geminiLiveUrl());
      ws.binaryType = "arraybuffer";

      ws.addEventListener("open", () => {
        ws!.send(
          JSON.stringify({
            setup: {
              model: GEMINI_MODEL,
              generation_config: {
                response_modalities: ["AUDIO"],
                input_audio_transcription: {},
                output_audio_transcription: {},
              },
              system_instruction: { parts: [{ text: DOPE_SYSTEM_PROMPT }] },
              tools: DOPE_TOOLS,
            },
          }),
        );
      });

      ws.addEventListener("message", (ev) => {
        if (typeof ev.data === "string") handleMessage(ev.data);
      });

      ws.addEventListener("close", (ev) => {
        console.debug("[gemini-live] close", ev.code, ev.reason);
        cleanup();
        set("idle");
      });

      ws.addEventListener("error", () => {
        cleanup();
        set("error", "connection failed");
      });
    },

    stop() {
      ws?.close(1000, "client stop");
      cleanup();
      set("idle");
    },
  };
}
